(() => {
  if (window.TOS_STATUS_PAGINATION_LOADED) return;
  window.TOS_STATUS_PAGINATION_LOADED = true;

  const ROOT_SELECTOR = '[data-tos-status-pagination]';
  const GRID_SELECTOR = '[data-tos-status-grid]';
  const CARD_SELECTOR = ':scope > [data-tos-product-card]';
  const STATE_ORDER = ['available', 'poa', 'on_hold', 'sold'];
  const instances = new Set();
  const instanceByRoot = new WeakMap();
  const pendingRoots = new Set();
  let initializationFlushScheduled = false;

  const positiveInteger = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const normalizedBaseKey = (urlLike = window.location.href) => {
    const url = new URL(urlLike, window.location.origin);
    url.searchParams.delete('page');
    url.searchParams.delete('tos_page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.hash = '';
    return url.toString();
  };

  const requestedLogicalPage = () => {
    const params = new URL(window.location.href).searchParams;
    return positiveInteger(params.get('tos_page') || params.get('page'), 1);
  };

  const cardsFromGrid = (grid, importNodes = false) => {
    if (!grid) return [];
    return Array.from(grid.querySelectorAll(CARD_SELECTOR)).map((card) => (
      importNodes ? document.importNode(card, true) : card
    ));
  };

  const snapshotCard = (card) => ({
    productId: card.dataset.tosProductId,
    state: card.dataset.tosProductState || 'available',
    html: card.outerHTML
  });

  const chunk = (items, size, type) => {
    const pages = [];
    for (let index = 0; index < items.length; index += size) {
      pages.push({
        type,
        cards: items.slice(index, index + size),
        groupPage: pages.length + 1
      });
    }
    return pages;
  };

  const pageTokens = (total, current) => {
    if (window.matchMedia('(max-width: 479px)').matches) {
      const pages = new Set([1, current, total]);
      const sorted = Array.from(pages)
        .filter((page) => page >= 1 && page <= total)
        .sort((a, b) => a - b);

      const tokens = [];
      sorted.forEach((page, index) => {
        if (index > 0 && page - sorted[index - 1] > 1) tokens.push(null);
        tokens.push(page);
      });
      return tokens;
    }

    if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

    const pages = new Set([1, total, current - 2, current - 1, current, current + 1, current + 2]);
    const sorted = Array.from(pages)
      .filter((page) => page >= 1 && page <= total)
      .sort((a, b) => a - b);

    const tokens = [];
    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) tokens.push(null);
      tokens.push(page);
    });
    return tokens;
  };

  const arrowIcon = (direction) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('stroke-linecap', 'butt');
    svg.setAttribute('stroke-linejoin', 'miter');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute(
      'class',
      `tos-arrow icon-theme icon-theme-stroke icon-core-chevron-${direction}`
    );
    svg.setAttribute('viewBox', '0 0 24 24');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', direction === 'left' ? 'M20 12H4M9 7l-5 5 5 5' : 'M4 12h16M15 7l5 5-5 5');
    svg.append(path);
    return svg;
  };

  class StatusPaginator {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector(GRID_SELECTOR);
      this.nativePagination = root.querySelector('[data-tos-native-pagination]');
      this.logicalPagination = root.querySelector('[data-tos-logical-pagination]');
      this.liveRegion = root.querySelector('[data-tos-pagination-live]');
      this.sectionId = root.dataset.tosSectionId;
      this.totalProducts = positiveInteger(this.grid?.dataset.tosTotalProducts, 0);
      this.pageSize = positiveInteger(this.grid?.dataset.tosPageSize, 1);
      this.nativePages = positiveInteger(this.grid?.dataset.tosNativePages, 1);
      this.nativeCurrentPage = positiveInteger(this.grid?.dataset.tosNativeCurrentPage, 1);
      this.maxProducts = positiveInteger(this.grid?.dataset.tosMaxProducts, 250);
      this.baseUrl = new URL(window.location.href);
      this.baseUrl.searchParams.delete('page');
      this.baseUrl.searchParams.delete('tos_page');
      this.baseUrl.searchParams.delete('section_id');
      this.baseUrl.searchParams.delete('sections');
      this.baseKey = normalizedBaseKey(this.baseUrl);
      this.logicalPages = [];
      this.currentLogicalPage = 1;
      this.ready = false;
      this.destroyed = false;
      this.abortController = new AbortController();
    }

    isCurrent() {
      return !this.destroyed
        && this.root.isConnected
        && this.grid?.isConnected
        && this.root.querySelector(GRID_SELECTOR) === this.grid
        && instanceByRoot.get(this.root) === this
        && normalizedBaseKey() === this.baseKey;
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.ready = false;
      this.abortController.abort();
      instances.delete(this);
    }

    async initialize() {
      if (!this.grid || !this.sectionId || this.totalProducts < 1) return;
      if (this.totalProducts > this.maxProducts) {
        this.root.dataset.tosPaginationState = 'native-fallback';
        return;
      }

      this.root.dataset.tosPaginationState = 'loading';
      this.grid.setAttribute('aria-busy', 'true');

      try {
        const cardsByNativePage = new Map();
        cardsByNativePage.set(this.nativeCurrentPage, cardsFromGrid(this.grid));

        const pagesToFetch = [];
        for (let page = 1; page <= this.nativePages; page += 1) {
          if (page !== this.nativeCurrentPage) pagesToFetch.push(page);
        }

        let nextPageIndex = 0;
        const worker = async () => {
          while (nextPageIndex < pagesToFetch.length) {
            const page = pagesToFetch[nextPageIndex];
            nextPageIndex += 1;
            cardsByNativePage.set(page, await this.fetchNativePage(page));
          }
        };

        const workerCount = Math.min(4, pagesToFetch.length);
        await Promise.all(Array.from({ length: workerCount }, () => worker()));

        if (!this.isCurrent()) return;

        const uniqueCards = new Map();
        for (let page = 1; page <= this.nativePages; page += 1) {
          const cards = cardsByNativePage.get(page) || [];
          cards.forEach((card) => {
            const productId = card.dataset.tosProductId;
            if (productId && !uniqueCards.has(productId)) {
              uniqueCards.set(productId, snapshotCard(card));
            }
          });
        }

        if (uniqueCards.size !== this.totalProducts) {
          throw new Error(
            `Expected ${this.totalProducts} unique products, received ${uniqueCards.size}.`
          );
        }

        const buckets = {
          available: [],
          poa: [],
          on_hold: [],
          sold: []
        };

        uniqueCards.forEach((card) => {
          (buckets[card.state] || buckets.available).push(card);
        });

        const nonSold = STATE_ORDER
          .filter((state) => state !== 'sold')
          .flatMap((state) => buckets[state]);

        const orderedCards = [...nonSold, ...buckets.sold];
        this.logicalPages = chunk(orderedCards, this.pageSize, 'active').map((page, index) => {
          const pageStart = index * this.pageSize;
          const soldStartIndex = buckets.sold.length > 0
            && nonSold.length >= pageStart
            && nonSold.length < pageStart + page.cards.length
              ? nonSold.length - pageStart
              : -1;
          const containsSold = page.cards.some((card) => card.state === 'sold');

          return {
            ...page,
            type: containsSold ? 'sold' : 'active',
            soldStartIndex
          };
        });

        if (this.logicalPages.length === 0) {
          this.logicalPages = [{ type: 'active', cards: [], groupPage: 1 }];
        }

        this.ready = true;
        this.root.dataset.tosPaginationState = 'ready';
        if (this.nativePagination) this.nativePagination.hidden = true;

        const initialPage = Math.min(requestedLogicalPage(), this.logicalPages.length);
        this.render(initialPage, { focus: false, replaceUrl: true });
      } catch (error) {
        if (!this.destroyed && instanceByRoot.get(this.root) === this) {
          this.fallback(error);
        }
      } finally {
        if (!this.destroyed && this.grid) this.grid.setAttribute('aria-busy', 'false');
      }
    }

    async fetchNativePage(page) {
      const url = new URL(this.baseUrl);
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Collection page ${page} returned HTTP ${response.status}.`);
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fetchedGrid = parsed.querySelector(GRID_SELECTOR);

      if (!fetchedGrid) {
        throw new Error(`Collection page ${page} did not contain a product grid.`);
      }

      return cardsFromGrid(fetchedGrid, true);
    }

    hrefFor(page) {
      const url = new URL(this.baseUrl);
      if (page > 1) url.searchParams.set('tos_page', String(page));
      return url.toString();
    }

    render(page, options = {}) {
      if (!this.ready || !this.grid) return;

      const targetPage = Math.max(1, Math.min(page, this.logicalPages.length));
      const logicalPage = this.logicalPages[targetPage - 1];

      this.grid.replaceChildren();

      if (logicalPage.cards.length > 0) {
        logicalPage.cards.forEach((card, index) => {
          if (index === logicalPage.soldStartIndex) {
            const divider = document.createElement('div');
            divider.className = 'js-grid__full-row collection__archive-divider';
            divider.dataset.tosLogicalDivider = '';
            const label = document.createElement('span');
            label.textContent = 'Previously Sold';
            divider.append(label);
            this.grid.append(divider);
          }

          const template = document.createElement('template');
          template.innerHTML = card.html;
          this.grid.append(template.content);
        });
      }
      this.currentLogicalPage = targetPage;
      this.renderPagination();

      const targetUrl = this.hrefFor(targetPage);
      if (options.replaceUrl) {
        window.history.replaceState({}, '', targetUrl);
      } else if (options.pushUrl) {
        window.history.pushState({}, '', targetUrl);
      }

      if (this.liveRegion) {
        const prefix = logicalPage.type === 'sold' ? 'Previously Sold. ' : '';
        this.liveRegion.textContent =
          `${prefix}Showing page ${targetPage} of ${this.logicalPages.length}.`;
      }

      if (options.focus) {
        this.grid.focus({ preventScroll: true });
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.root.scrollIntoView({
          block: 'start',
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }
    }

    renderPagination() {
      if (!this.logicalPagination) return;

      const totalPages = this.logicalPages.length;
      this.logicalPagination.replaceChildren();

      if (totalPages <= 1) {
        this.logicalPagination.hidden = true;
        return;
      }

      this.logicalPagination.hidden = false;
      this.logicalPagination.setAttribute('role', 'navigation');
      this.logicalPagination.setAttribute('aria-label', 'Collection pages');

      const list = document.createElement('ul');
      list.className = 'pagination-custom';

      list.append(this.arrowItem('Previous page', this.currentLogicalPage - 1));
      list.append(this.spacerItem());

      pageTokens(totalPages, this.currentLogicalPage).forEach((token) => {
        const item = document.createElement('li');

        if (token === null) {
          item.className = 'pagination-custom__num pagination-custom__num--dots';
          const dots = document.createElement('span');
          dots.textContent = '…';
          item.append(dots);
        } else if (token === this.currentLogicalPage) {
          item.className = 'pagination-custom__num active';
          const current = document.createElement('span');
          current.setAttribute('aria-current', 'page');
          current.textContent = String(token);
          item.append(current);
        } else {
          item.className = 'pagination-custom__num';
          const link = document.createElement('a');
          link.href = this.hrefFor(token);
          link.dataset.tosPage = String(token);
          link.setAttribute('aria-label', `Page ${token}`);
          link.textContent = String(token);
          item.append(link);
        }

        list.append(item);
      });

      list.append(this.spacerItem());
      list.append(this.arrowItem('Next page', this.currentLogicalPage + 1));

      list.addEventListener('click', (event) => {
        const link = event.target.closest('[data-tos-page]');
        if (!link) return;
        event.preventDefault();
        event.stopPropagation();
        this.render(positiveInteger(link.dataset.tosPage, 1), {
          focus: true,
          pushUrl: true
        });
      });

      this.logicalPagination.append(list);
    }

    arrowItem(label, targetPage) {
      const item = document.createElement('li');
      item.className = 'pagination-custom__arr';
      const direction = label.startsWith('Previous') ? 'left' : 'right';

      if (targetPage < 1 || targetPage > this.logicalPages.length) {
        item.classList.add('disabled');
        const disabled = document.createElement('span');
        disabled.append(arrowIcon(direction));
        item.append(disabled);
        return item;
      }

      const link = document.createElement('a');
      link.href = this.hrefFor(targetPage);
      link.dataset.tosPage = String(targetPage);
      link.setAttribute('aria-label', label);
      link.append(arrowIcon(direction));
      item.append(link);
      return item;
    }

    spacerItem() {
      const spacer = document.createElement('li');
      spacer.className = 'pagination-custom__spacer';
      const content = document.createElement('span');
      content.innerHTML = '&#8203;';
      spacer.append(content);
      return spacer;
    }

    fallback(error) {
      this.ready = false;
      this.root.dataset.tosPaginationState = 'native-fallback';
      if (this.nativePagination) this.nativePagination.hidden = false;
      if (this.logicalPagination) {
        this.logicalPagination.hidden = true;
        this.logicalPagination.replaceChildren();
      }
      console.warn('[Three Over Six] Status pagination fell back to Shopify pagination.', error);
    }
  }

  const initializeRoot = (root) => {
    if (!root?.isConnected) return;

    const grid = root.querySelector(GRID_SELECTOR);
    if (!grid) return;

    const existingInstance = instanceByRoot.get(root);
    if (existingInstance && !existingInstance.destroyed && existingInstance.grid === grid) return;
    if (existingInstance) existingInstance.destroy();

    const instance = new StatusPaginator(root);
    instanceByRoot.set(root, instance);
    instances.add(instance);
    instance.initialize();
  };

  const initializeWithin = (scope = document) => {
    const roots = new Set();

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.add(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => roots.add(root));
    }

    roots.forEach((root) => initializeRoot(root));
  };

  const flushPendingRoots = () => {
    initializationFlushScheduled = false;
    const roots = Array.from(pendingRoots);
    pendingRoots.clear();
    roots.forEach((root) => initializeRoot(root));
  };

  const scheduleRootInitialization = (root) => {
    if (!root) return;
    pendingRoots.add(root);
    if (initializationFlushScheduled) return;
    initializationFlushScheduled = true;
    Promise.resolve().then(flushPendingRoots);
  };

  const scheduleFromAddedNode = (node) => {
    if (!(node instanceof Element)) return;

    if (node.matches(ROOT_SELECTOR)) scheduleRootInitialization(node);
    if (node.matches(GRID_SELECTOR)) {
      scheduleRootInitialization(node.closest(ROOT_SELECTOR));
    }

    node.querySelectorAll(ROOT_SELECTOR).forEach((root) => scheduleRootInitialization(root));
    node.querySelectorAll(GRID_SELECTOR).forEach((grid) => {
      scheduleRootInitialization(grid.closest(ROOT_SELECTOR));
    });
  };

  const start = () => {
    initializeWithin(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          scheduleFromAddedNode(node);
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    window.addEventListener('popstate', () => {
      instances.forEach((instance) => {
        if (!instance.root.isConnected) {
          instance.destroy();
          if (instanceByRoot.get(instance.root) === instance) {
            instanceByRoot.delete(instance.root);
          }
          return;
        }

        if (instance.ready && instance.isCurrent()) {
          instance.render(requestedLogicalPage(), { focus: false });
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
